import React from 'react';

import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import MicrophoneIcon from '@atlaskit/icon/core/microphone';

import type { AiSpeechToTextPlugin } from './aiSpeechToTextPluginType';
import { aiSpeechToTextPluginKey } from './pluginKey';
import type { AISpeechToTextPluginState } from './types/AISpeechToTextPluginState';
import { AISpeechToTextContent } from './ui/components/AISpeechToTextContent';

// This CLOUD_ID works only for internal usage and POC purposes. Do not use it in production or share it externally.
// TODO: We need to implement here a logic to get the current user's cloudId.
const CLOUD_ID_INTERNAL_USE_ONLY = 'a436116f-02ce-4520-8fbb-7301462a1674';

const EMPTY_STATE: AISpeechToTextPluginState = {
	isListening: false,
	error: null,
};

export const aiSpeechToTextPlugin: AiSpeechToTextPlugin = ({ api }) => {
	return {
		name: 'aiSpeechToText',

		getSharedState(editorState) {
			if (!editorState) {
				return EMPTY_STATE;
			}
			return aiSpeechToTextPluginKey.getState(editorState) ?? EMPTY_STATE;
		},

		pmPlugins() {
			return [
				{
					name: 'speechToText',
					plugin: () =>
						new SafePlugin<AISpeechToTextPluginState>({
							key: aiSpeechToTextPluginKey,
							state: {
								init(): AISpeechToTextPluginState {
									return EMPTY_STATE;
								},
								apply(
									tr: ReadonlyTransaction,
									oldPluginState: AISpeechToTextPluginState,
								): AISpeechToTextPluginState {
									const meta = tr.getMeta(aiSpeechToTextPluginKey);
									if (meta) {
										return { ...oldPluginState, ...meta };
									}
									return oldPluginState;
								},
							},
						}),
				},
			];
		},

		pluginsOptions: {
			quickInsert: () => [
				{
					id: 'aiSpeechToText',
					title: 'Speech to text',
					description: 'Dictate text using your voice',
					priority: 600,
					keywords: ['speech', 'voice', 'dictation', 'microphone', 'audio', 'speak', 'stt'],
					icon: () => <MicrophoneIcon label="AI Speech to Text" />,
					action: (insert) => {
						const tr = insert('');
						return tr.setMeta(aiSpeechToTextPluginKey, { isListening: true });
					},
				},
			],
		},

		contentComponent() {
			return <AISpeechToTextContent cloudId={CLOUD_ID_INTERNAL_USE_ONLY} api={api} />;
		},
	};
};
